import { Col, Form, Input, InputNumber, Modal, Radio, Row, Select } from "antd";
import React, { useEffect, useMemo, useState } from "react";

import { TextAreaInputBox } from "../../../components/InputField";
import SingleImageUpload from "../../../components/SingleImageUpload";
import lang from "../../../helper/langHelper";
import { Severty, ShowToast } from "../../../helper/toast";
import useRequest from "../../../hooks/useRequest";

const FileType = ["image/png", "image/jpg", "image/jpeg", "image/avif", "image/webp", "image/gif"];

const normalizeOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      value: item?._id || item?.id || item?.value || item,
      label: item?.label || item?.name || item?.title || item?.slug || item?.value || item?._id || item,
      raw: item?.raw || item,
    }))
    .filter((item) => item.value);

const normalizeIdList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => item?._id || item?.id || item?.value || item).filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const AddForm = ({ api, show, hide, data, refresh, categoryOptions = [], subCategoryOptions = [], occasionOptions = [], keywordOptions = [], digitalDocumentOptions = [] }) => {
  const [form] = Form.useForm();
  const productType = Form.useWatch("productType", form);
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [thumbnail, setThumbnail] = useState(null);
  const [isThumbnailRemove, setIsThumbnailRemove] = useState(false);

  const filteredSubCategoryOptions = useMemo(() => {
    if (!selectedCategoryIds.length) {
      return normalizeOptions(subCategoryOptions);
    }

    return normalizeOptions(subCategoryOptions).filter((item) => Array.isArray(item.raw?.category) && item.raw.category.some((category) => selectedCategoryIds.includes(category)));
  }, [selectedCategoryIds, subCategoryOptions]);

  useEffect(() => {
    if (!data) {
      form.resetFields();
      setSelectedCategoryIds([]);
      setThumbnail(null);
      setIsThumbnailRemove(false);
      return;
    }

    const categoryIds = normalizeIdList(data?.category_ids);
    const subCategoryIds = normalizeIdList(data?.sub_category_ids);
    const occasionIds = normalizeIdList(data?.occasion_ids);
    const keywordIds = normalizeIdList(data?.keywords_ids);
    const digitalDocumentIds = normalizeIdList(data?.digitalDocuments);

    form.setFieldsValue({
      ...data,
      category_ids: categoryIds,
      sub_category_ids: subCategoryIds,
      occasion_ids: occasionIds,
      keywords_ids: keywordIds,
      digital_document_ids: digitalDocumentIds,
      productType: data?.productType || null,
      has_variants: data?.has_variants ?? false,
          is_active: data?.is_active ?? true,
          isCODAvailable: data?.isCODAvailable ?? true,
          price: data?.price ?? null,
      stock: data?.stock ?? null,
    });
    setSelectedCategoryIds(categoryIds);
    setThumbnail(data?.thumbnail || null);
    setIsThumbnailRemove(false);
  }, [data, form]);

  const handleCategoryChange = (values) => {
    const nextCategoryIds = Array.isArray(values) ? values : [];
    setSelectedCategoryIds(nextCategoryIds);

    if (!nextCategoryIds.length) {
      return;
    }

    const allowedSubCategoryIds = normalizeOptions(subCategoryOptions)
      .filter((item) => Array.isArray(item.raw?.category) && item.raw.category.some((category) => nextCategoryIds.includes(category)))
      .map((item) => item.value);

    const currentSubCategoryIds = form.getFieldValue("sub_category_ids") || [];
    const filteredValues = currentSubCategoryIds.filter((id) => allowedSubCategoryIds.includes(id));
    form.setFieldsValue({ sub_category_ids: filteredValues });
  };

  const handleThumbnailChange = (file) => {
    if (!file) {
      setThumbnail(null);
      setIsThumbnailRemove(Boolean(data));
      return;
    }

    setIsThumbnailRemove(false);
    setThumbnail(file);
  };

  const onCreate = (values) => {
    setLoading(true);
    const payload = new FormData();

    payload.append("title", values.title || "");
    payload.append("description", values.description || "");
    payload.append("category_ids", (values.category_ids || []).filter(Boolean).join(","));
    payload.append("sub_category_ids", (values.sub_category_ids || []).filter(Boolean).join(","));
    payload.append("occasion_ids", (values.occasion_ids || []).filter(Boolean).join(","));
    payload.append("keywords_ids", (values.keywords_ids || []).filter(Boolean).join(","));
    payload.append("digital_document_ids", values.productType === "digital" ? (values.digital_document_ids || []).filter(Boolean).join(",") : "");
    payload.append("productType", values.productType || "");
    payload.append("has_variants", values.productType === "digital" ? "false" : values.has_variants ? "true" : "false");
    payload.append("isCODAvailable", values.productType === "digital" ? "false" : values.isCODAvailable === false ? "false" : "true");
    payload.append("is_active", values.is_active ?? true);
    payload.append("price", values.price ?? "");
    payload.append("stock", values.productType === "digital" ? "" : values.stock ?? "");

    const selectedFile = thumbnail instanceof File ? thumbnail : thumbnail?.originFileObj;
    if (selectedFile instanceof File) {
      payload.append("image", selectedFile);
    }

    if (data && isThumbnailRemove) {
      payload.append("isThumbnailRemove", "true");
    }

    request({
      url: `${data ? `${api.addEdit}/${data._id}` : api.addEdit}`,
      method: data ? "PUT" : "POST",
      data: payload,
      header: {
        "Content-Type": "multipart/form-data",
      },
      onSuccess: (response) => {
        setLoading(false);
        if (response.status) {
          ShowToast(response.message, Severty.SUCCESS);
          hide();
          refresh();
        } else {
          ShowToast(response.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error?.response?.data?.message || error?.message || "Something went wrong", Severty.ERROR);
      },
    });
  };

  return (
    <Modal
      open={show}
      width={1100}
      okText={data ? lang("Update") : lang("Add")}
      cancelText={lang("Cancel")}
      onCancel={hide}
      okButtonProps={{
        form: "product-form",
        htmlType: "submit",
        loading,
      }}
      centered
      className="tab_modal"
    >
      <Form
        id="product-form"
        form={form}
        onFinish={onCreate}
        layout="vertical"
        initialValues={{
          productType: null,
          has_variants: false,
          is_active: true,
          category_ids: [],
          sub_category_ids: [],
          occasion_ids: [],
          keywords_ids: [],
          digital_document_ids: [],
          price: null,
          stock: null,
          isCODAvailable: true,
        }}
      >
        <h4 className="modal_title_cls">{data ? lang("Edit Product") : lang("Add New Product")}</h4>
        <Row gutter={[16, 0]}>
          <Col span={24} md={12}>
            <Form.Item
              label={lang("Title")}
              name="title"
              rules={[
                { required: true, message: lang("Title is required") },
                { min: 2, message: lang("Title should contain at least 2 characters!") },
                { max: 10000, message: lang("Title should not contain more then 10000 characters!") },
              ]}
              normalize={(value) => value?.trimStart?.() ?? value}
            >
              <Input autoComplete="off" maxLength={10000} placeholder={lang("Enter Title")} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Product Type")} name="productType">
              <Select allowClear placeholder={lang("Select Product Type")} getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                <Select.Option value="own">{lang("Own Product")}</Select.Option>
                <Select.Option value="affiliate">{lang("Affiliate Product")}</Select.Option>
                <Select.Option value="digital">{lang("Digital Product")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <TextAreaInputBox
            label={lang("Description")}
            name="description"
            placeholder={lang("Enter Description")}
            inputProps={{ rows: 4, maxLength: 2000 }}
            colProps={{ xs: 24, md: 24 }}
          />

          <Col span={24} md={12}>
            <Form.Item label={lang("Categories")} name="category_ids">
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder={lang("Select Categories")}
                optionFilterProp="label"
                onChange={handleCategoryChange}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              >
                {normalizeOptions(categoryOptions).map((item) => (
                  <Select.Option key={item.value} value={item.value} label={item.label}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Sub Categories")} name="sub_category_ids">
              <Select mode="multiple" allowClear showSearch placeholder={lang("Select Sub Categories")} optionFilterProp="label" getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                {filteredSubCategoryOptions.map((item) => (
                  <Select.Option key={item.value} value={item.value} label={item.label}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Occasions")} name="occasion_ids">
              <Select mode="multiple" allowClear showSearch placeholder={lang("Select Occasions")} optionFilterProp="label" getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                {normalizeOptions(occasionOptions).map((item) => (
                  <Select.Option key={item.value} value={item.value} label={item.label}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Keywords")} name="keywords_ids">
              <Select mode="multiple" allowClear showSearch placeholder={lang("Select Keywords")} optionFilterProp="label" getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                {normalizeOptions(keywordOptions).map((item) => (
                  <Select.Option key={item.value} value={item.value} label={item.label}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item label={lang("Price")} name="price">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={lang("Enter Price")} />
            </Form.Item>
          </Col>

          {productType !== "digital" ? (
            <Col span={24} md={8}>
              <Form.Item label={lang("Stock")} name="stock">
                <InputNumber min={0} style={{ width: "100%" }} placeholder={lang("Enter Stock")} />
              </Form.Item>
            </Col>
          ) : null}

          {productType !== "digital" ? (
            <Col span={24} md={8}>
              <Form.Item label={lang("Has Variants")} name="has_variants">
                <Radio.Group>
                  <Radio value={true}>{lang("Yes")}</Radio>
                  <Radio value={false}>{lang("No")}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          ) : null}

          {productType !== "digital" ? (
            <Col span={24} md={8}>
              <Form.Item label={lang("Cash On Delivery Available")} name="isCODAvailable">
                <Radio.Group>
                  <Radio value={true}>{lang("Yes")}</Radio>
                  <Radio value={false}>{lang("No")}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          ) : null}

          {productType === "digital" ? (
            <Col span={24}>
              <Form.Item label="Digital Documents" name="digital_document_ids" rules={[{ required: true, message: "Please select at least one digital document" }]}>
                <Select mode="multiple" allowClear showSearch placeholder="Search and select uploaded PDF documents" optionFilterProp="label" getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                  {normalizeOptions(digitalDocumentOptions).map((item) => (
                    <Select.Option key={item.value} value={item.value} label={item.label}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          ) : null}

          <Col span={24} md={12}>
            <Form.Item label={lang("Status")} name="is_active">
              <Radio.Group>
                <Radio value={true}>{lang("Active")}</Radio>
                <Radio value={false}>{lang("De-Active")}</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Thumbnail")} name="thumbnail">
              <SingleImageUpload value={thumbnail} fileType={FileType} btnName={"Thumbnail"} imageType="product" onChange={handleThumbnailChange} size={5} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddForm;
